import React, { useState } from "react";
import { Link, useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";

export default function Register() {
  const { user, register, loading } = useAuth();
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "mother" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const navigate = useNavigate();

  if (!loading && user) return <Navigate to="/app/onboarding" replace />;

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      await register(form);
      toast.success("Welcome to Vansha.");
      navigate("/app/onboarding");
    } catch (e) {
      const msg = apiErrorMessage(e.response?.data?.detail) || e.message;
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2" data-testid="register-page">
      <div className="flex items-center justify-center p-8 md:p-16 order-2 md:order-1">
        <div className="w-full max-w-sm">
          <Link to="/" className="md:hidden font-serif text-3xl text-[#1B1533] block mb-8">Vansha</Link>
          <p className="overline mb-3 text-saffron">Begin your journey</p>
          <h1 className="font-serif text-4xl text-[#1B1533] mb-2">Create your space</h1>
          <p className="text-[#3D2E5C] mb-10">It begins with a quiet hello.</p>
          <form onSubmit={onSubmit} className="space-y-7" data-testid="register-form">
            <div>
              <label className="overline mb-2 block">Your name</label>
              <input type="text" required value={form.name} onChange={onChange("name")} className="input-line" placeholder="Aanya Sharma" data-testid="register-name" />
            </div>
            <div>
              <label className="overline mb-2 block">Email</label>
              <input type="email" required value={form.email} onChange={onChange("email")} className="input-line" placeholder="you@home.com" data-testid="register-email" />
            </div>
            <div>
              <label className="overline mb-2 block">Password</label>
              <input type="password" required minLength={6} value={form.password} onChange={onChange("password")} className="input-line" placeholder="At least 6 characters" data-testid="register-password" />
            </div>
            <div>
              <label className="overline mb-2 block">Your role</label>
              <select value={form.role} onChange={onChange("role")} className="input-line bg-transparent" data-testid="register-role">
                <option value="mother">Expecting / new mother</option>
                <option value="father">Father / partner</option>
                <option value="grandparent">Grandparent</option>
                <option value="family">Family member</option>
              </select>
            </div>
            {err && <p className="text-sm text-terracotta" data-testid="register-error">{err}</p>}
            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="register-submit-button">
              {busy ? "Creating…" : "Create my Vansha"}
            </button>
            <p className="text-sm text-[#7A6E96] text-center">
              Already a member? <Link to="/login" className="text-terracotta underline-offset-2 hover:underline" data-testid="register-go-login">Sign in</Link>
            </p>
          </form>
        </div>
      </div>
      <div className="hidden md:block relative order-1 md:order-2 bg-[#1B1533]">
        <div className="absolute inset-0 flex items-center justify-center">
          <svg viewBox="0 0 400 400" className="w-2/3 h-auto opacity-90">
            {[0,60,120,180].map((r,i)=><path key={r} d={`M 40 ${200-r+100} Q 200 ${100-r} 360 ${200-r+100}`} fill="none" stroke={["#E8552B","#F4A72C","#7BA098","#FFFFFF"][i]} strokeWidth="1.5" opacity={0.6-i*0.1} />)}
          </svg>
        </div>
        <Link to="/" className="absolute top-6 right-6 font-serif text-3xl text-white">Vansha</Link>
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <p className="font-serif text-3xl leading-snug max-w-md italic">A place for memories that should never be lost to time.</p>
        </div>
      </div>
    </div>
  );
}
